import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../../lib/axios'
import {
  Megaphone, Users, Clock, ArrowUpRight,
  RefreshCw, Inbox, CheckCircle, Pause, FileText, Zap, Mail,
  ShieldCheck, ShieldAlert, ShieldOff
} from 'lucide-react'
import '../Pages.css'
import './Dashboard.css'

const fmt = (n) => (n ?? 0).toLocaleString()

function BounceChart({ points, threshold = 5, height = 160 }) {
  const padding = { top: 14, right: 12, bottom: 22, left: 32 }
  const w = 600
  const h = height
  const innerW = w - padding.left - padding.right
  const innerH = h - padding.top - padding.bottom

  const maxRate = Math.max(threshold * 1.4, ...points.map(p => p.bounce_rate || 0), 1)
  const yScale = (v) => padding.top + innerH * (1 - v / maxRate)
  const step = points.length > 1 ? innerW / (points.length - 1) : 0

  const coords = points.map((p, i) => ({
    x: padding.left + i * step,
    y: yScale(p.bounce_rate || 0),
    v: p.bounce_rate || 0,
    ts: p.ts,
  }))

  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(2)} ${c.y.toFixed(2)}`).join(' ')
  const area = coords.length
    ? `${line} L ${coords[coords.length - 1].x.toFixed(2)} ${padding.top + innerH} L ${coords[0].x.toFixed(2)} ${padding.top + innerH} Z`
    : ''
  const thresholdY = yScale(threshold)
  const lastCoord = coords[coords.length - 1]
  const breached = lastCoord && lastCoord.v >= threshold
  const lineColor = breached ? '#ef4444' : '#3b82f6'

  const tickValues = [0, threshold / 2, threshold, Math.ceil(maxRate)]
    .filter((v, i, a) => a.indexOf(v) === i && v <= maxRate)

  const fmtTs = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="bounce-svg" style={{ height }}>
      <defs>
        <linearGradient id="bounce-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.22" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
        </linearGradient>
      </defs>

      {tickValues.map(v => (
        <g key={v}>
          <line
            x1={padding.left} x2={w - padding.right}
            y1={yScale(v)} y2={yScale(v)}
            stroke="currentColor" strokeOpacity="0.07" strokeWidth="1"
          />
          <text
            x={padding.left - 6} y={yScale(v) + 3}
            textAnchor="end" fontSize="9"
            fill="currentColor" opacity="0.45"
            fontFamily="ui-monospace, monospace"
          >
            {v}%
          </text>
        </g>
      ))}

      <line
        x1={padding.left} x2={w - padding.right}
        y1={thresholdY} y2={thresholdY}
        stroke="#ef4444" strokeWidth="1" strokeDasharray="4 4" opacity="0.7"
      />
      <text
        x={w - padding.right - 4} y={thresholdY - 4}
        textAnchor="end" fontSize="9"
        fill="#ef4444" opacity="0.85"
        fontFamily="ui-monospace, monospace"
      >
        threshold {threshold}%
      </text>

      {area && <path d={area} fill="url(#bounce-fill)" />}
      {line && <path d={line} stroke={lineColor} strokeWidth="1.75" fill="none" vectorEffect="non-scaling-stroke" />}

      {lastCoord && (
        <>
          <circle cx={lastCoord.x} cy={lastCoord.y} r="4" fill={lineColor} opacity="0.25" />
          <circle cx={lastCoord.x} cy={lastCoord.y} r="2.5" fill={lineColor} />
        </>
      )}

      {coords.length > 0 && (
        <>
          <text
            x={coords[0].x} y={h - 6}
            textAnchor="start" fontSize="9"
            fill="currentColor" opacity="0.45"
            fontFamily="ui-monospace, monospace"
          >
            {fmtTs(coords[0].ts)}
          </text>
          <text
            x={coords[coords.length - 1].x} y={h - 6}
            textAnchor="end" fontSize="9"
            fill="currentColor" opacity="0.45"
            fontFamily="ui-monospace, monospace"
          >
            now
          </text>
        </>
      )}
    </svg>
  )
}

function Sparkline({ points, height = 64, color = '#6366f1' }) {
  const padding = 4
  const max = Math.max(1, ...points.map(p => p.value))
  const w = 100
  const h = height
  const step = points.length > 1 ? (w - padding * 2) / (points.length - 1) : 0

  const coords = points.map((p, i) => ({
    x: padding + i * step,
    y: padding + (h - padding * 2) * (1 - p.value / max),
  }))

  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(2)} ${c.y.toFixed(2)}`).join(' ')
  const area = `${line} L ${coords[coords.length - 1].x.toFixed(2)} ${h - padding} L ${coords[0].x.toFixed(2)} ${h - padding} Z`

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="spark-svg">
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#spark-fill)" />
      <path d={line} stroke={color} strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" />
      {coords.length > 0 && (
        <circle
          cx={coords[coords.length - 1].x}
          cy={coords[coords.length - 1].y}
          r="2"
          fill={color}
        />
      )}
    </svg>
  )
}

function Dashboard() {
  const [data, setData] = useState(null)
  const [series, setSeries] = useState(null)
  const [shield, setShield] = useState(null)
  const [shieldSeries, setShieldSeries] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const fetchAll = async () => {
    try {
      const [summary, ts, bs, bts] = await Promise.all([
        axios.get('dashboard/summary'),
        axios.get('dashboard/timeseries', { params: { days: 7 } }).catch(() => ({ data: { points: [] } })),
        axios.get('dashboard/bounceshield').catch(() => ({ data: null })),
        axios.get('dashboard/bounceshield/timeseries', { params: { hours: 24 } }).catch(() => ({ data: { points: [], threshold: 5 } })),
      ])
      setData(summary.data)
      setSeries(ts.data)
      setShield(bs.data)
      setShieldSeries(bts.data)
    } catch (err) {
      console.error('Dashboard fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    fetchAll()
    const id = setInterval(fetchAll, 30000)
    return () => clearInterval(id)
  }, [])

  const sparkPoints = useMemo(() => {
    const pts = series?.points || []
    return pts.map(p => ({ label: p.date, value: p.sent }))
  }, [series])

  if (loading || !data) {
    return (
      <div className="dash-shell">
        <div className="dash-hero-skel">
          {[1,2,3,4].map(i => <div key={i} className="kpi-skel" />)}
        </div>
        <div className="dash-card-skel" />
      </div>
    )
  }

  const { today, campaigns, total_campaigns, total_contacts, accounts, queue, active_campaigns, recent_replies } = data
  const sentToday = today.sent || 0
  const bouncedToday = today.bounced || 0
  const bounceRate = sentToday ? (bouncedToday / sentToday) * 100 : 0
  const deliveredRate = sentToday ? ((sentToday - bouncedToday) / sentToday) * 100 : 0
  const replyRate = sentToday ? (today.replies / sentToday) * 100 : 0
  const totalSentWeek = (series?.points || []).reduce((a, p) => a + (p.sent || 0), 0)
  const shieldStatus = shield?.overall_status || 'protected'
  const ShieldIcon = shieldStatus === 'critical' ? ShieldOff : shieldStatus === 'watching' ? ShieldAlert : ShieldCheck
  const shieldLabel = shieldStatus === 'critical'
    ? `BounceShield · ${shield.auto_paused_count} paused`
    : shieldStatus === 'watching'
    ? `BounceShield · watching ${shield.worst_bounce_rate}%`
    : 'BounceShield · Protected'

  return (
    <div className="dash-shell">
      {/* ── Hero header ── */}
      <header className="dash-hero">
        <div>
          <div className="dash-eyebrow">overview</div>
          <h1 className="dash-title">Welcome back</h1>
          <p className="dash-sub">
            {fmt(totalSentWeek)} emails sent · {fmt(total_contacts)} contacts · {accounts.active}/{accounts.total} accounts live
          </p>
        </div>
        <div className="dash-actions">
          <span className={`status-pill shield-${shieldStatus}`}>
            <ShieldIcon size={12} strokeWidth={2.25} />
            {shieldLabel}
          </span>
          <button className="btn-ghost-mini" onClick={fetchAll}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </header>

      {/* ── KPI Hero Row ── */}
      <div className="kpi-row">
        <KpiTile
          label="Sent"
          value={fmt(sentToday)}
          delta={`+${fmt(today.opens)} opens`}
          tone="primary"
        />
        <KpiTile
          label="Delivered"
          value={fmt(sentToday - bouncedToday)}
          delta={`${deliveredRate.toFixed(1)}%`}
          tone="success"
        />
        <KpiTile
          label="Bounce rate"
          value={`${bounceRate.toFixed(1)}%`}
          delta={`${fmt(bouncedToday)} today`}
          tone={bounceRate >= 3.5 ? 'danger' : 'warn'}
        />
        <KpiTile
          label="Replies"
          value={fmt(today.replies)}
          delta={`${replyRate.toFixed(1)}%`}
          tone="info"
        />
      </div>

      {/* ── Chart card ── */}
      <section className="panel">
        <div className="panel-head">
          <div>
            <div className="panel-title">Sends · last 7 days</div>
            <div className="panel-sub">Daily volume across active campaigns</div>
          </div>
          <div className="panel-legend">
            <span><span className="dot dot-primary" /> Sent</span>
            <span className="legend-muted">Updated {new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
          </div>
        </div>
        <div className="panel-chart">
          {sparkPoints.length > 0 ? (
            <Sparkline points={sparkPoints} height={140} />
          ) : (
            <div className="empty-chart">No send data yet</div>
          )}
        </div>
        <div className="panel-axis">
          {sparkPoints.map((p, i) => (
            <span key={p.label} className="axis-label">
              {i === 0 || i === sparkPoints.length - 1 || i === Math.floor(sparkPoints.length / 2)
                ? new Date(p.label).toLocaleDateString([], { month: 'short', day: 'numeric' })
                : ''}
            </span>
          ))}
        </div>
      </section>

      {/* ── BounceShield live panel ── */}
      {shield && (
        <section className={`panel shield-panel shield-${shieldStatus}`}>
          <div className="panel-head">
            <div className="panel-title">
              <ShieldIcon size={14} strokeWidth={1.9} /> BounceShield
              <span className="live-pulse"><span className="live-dot" /> live</span>
            </div>
            <div className="panel-legend">
              <span className="legend-muted mono">threshold {shield.threshold}%</span>
            </div>
          </div>

          <div className="shield-board">
            <div className="shield-numbers">
              <div className="shield-big">
                <div className="shield-big-label">Current bounce rate</div>
                <div className={`shield-big-val tone-${shieldStatus}`}>
                  <span className="mono">{shield.worst_bounce_rate}</span><span className="shield-big-unit">%</span>
                </div>
                <div className="shield-big-sub mono">threshold {shield.threshold}%</div>
              </div>
              <div className="shield-mini-grid">
                <div className="shield-mini">
                  <div className="shield-mini-label">Status</div>
                  <div className={`shield-mini-val tone-${shieldStatus}`}>
                    {shieldStatus === 'critical' ? 'Auto-paused' : shieldStatus === 'watching' ? 'Watching' : 'Protected'}
                  </div>
                </div>
                <div className="shield-mini">
                  <div className="shield-mini-label">Auto-paused</div>
                  <div className="shield-mini-val mono">{shield.auto_paused_count}</div>
                </div>
                <div className="shield-mini">
                  <div className="shield-mini-label">Manual</div>
                  <div className="shield-mini-val mono">{shield.manually_paused_count}</div>
                </div>
                <div className="shield-mini">
                  <div className="shield-mini-label">Window</div>
                  <div className="shield-mini-val mono">24h</div>
                </div>
              </div>
            </div>
            <div className="shield-chart">
              <div className="shield-chart-head">
                <span className="mono shield-chart-title">Bounce rate · last 24h</span>
                <div className="shield-chart-legend">
                  <span><span className="dot" style={{background:'#3b82f6'}}/> rate</span>
                  <span><span className="dot dashed" style={{background:'#ef4444'}}/> auto-pause threshold</span>
                </div>
              </div>
              {shieldSeries?.points?.length ? (
                <BounceChart
                  points={shieldSeries.points}
                  threshold={shieldSeries.threshold || shield.threshold}
                  height={170}
                />
              ) : (
                <div className="empty-chart">No send activity in last 24h</div>
              )}
            </div>
          </div>

          {(shield.auto_paused.length > 0 || shield.active_health.length > 0) && (
            <div className="shield-lists">
              {shield.auto_paused.length > 0 && (
                <div className="shield-block">
                  <div className="shield-block-title">Currently auto-paused</div>
                  {shield.auto_paused.map(c => (
                    <div key={c.id} className="shield-row paused" onClick={() => navigate(`/campaigns/analytics/${c.id}`)}>
                      <Pause size={12} strokeWidth={2} />
                      <div className="shield-row-body">
                        <div className="shield-row-name">{c.name}</div>
                        <div className="shield-row-meta">{c.pause_reason}</div>
                      </div>
                      <div className="shield-row-time mono">
                        {c.paused_at ? new Date(c.paused_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {shield.active_health.length > 0 && (
                <div className="shield-block">
                  <div className="shield-block-title">Active campaign health</div>
                  {shield.active_health.map(c => {
                    const pct = Math.min(100, (c.bounce_rate / shield.threshold) * 100)
                    const tone = c.bounce_rate >= shield.threshold ? 'danger'
                      : c.bounce_rate >= shield.threshold * 0.7 ? 'warn' : 'ok'
                    return (
                      <div key={c.id} className="shield-row" onClick={() => navigate(`/campaigns/analytics/${c.id}`)}>
                        <div className="shield-row-body">
                          <div className="shield-row-name">{c.name}</div>
                          <div className="shield-meter">
                            <div className={`shield-meter-fill tone-${tone}`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="shield-row-meta">
                            <span className="mono">{c.bounce_rate}%</span> · {fmt(c.bounced)}/{fmt(c.sent)} bounced
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ── Quick counts strip ── */}
      <div className="strip">
        <StripCell label="Total campaigns" value={total_campaigns} icon={Megaphone} onClick={() => navigate('/campaigns')} />
        <StripCell label="Total contacts" value={fmt(total_contacts)} icon={Users} onClick={() => navigate('/contacts')} />
        <StripCell label="Active accounts" value={`${accounts.active}/${accounts.total}`} icon={Mail} onClick={() => navigate('/email-accounts')} />
        <StripCell label="In queue" value={fmt(queue.pending)} icon={Clock} />
      </div>

      {/* ── Campaign status row ── */}
      <section className="panel">
        <div className="panel-head">
          <div className="panel-title">Campaign status</div>
          <button className="link-btn" onClick={() => navigate('/campaigns')}>
            View all <ArrowUpRight size={12} />
          </button>
        </div>
        <div className="status-row">
          {[
            { key: 'active', label: 'Active', icon: Zap, tone: 'success' },
            { key: 'paused', label: 'Paused', icon: Pause, tone: 'warn' },
            { key: 'draft', label: 'Draft', icon: FileText, tone: 'muted' },
            { key: 'completed', label: 'Completed', icon: CheckCircle, tone: 'info' },
          ].map(s => (
            <div key={s.key} className={`status-cell tone-${s.tone}`}>
              <s.icon size={15} strokeWidth={1.75} />
              <div className="status-val">{campaigns[s.key] || 0}</div>
              <div className="status-lbl">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Activity feed style: replies + active campaigns ── */}
      <div className="dash-grid-2">
        <section className="panel">
          <div className="panel-head">
            <div className="panel-title">
              <Megaphone size={14} strokeWidth={1.75} /> Active campaigns
            </div>
            <span className="muted-tag">{active_campaigns.length} running</span>
          </div>
          <div className="feed">
            {active_campaigns.length === 0 ? (
              <div className="empty">No active campaigns right now</div>
            ) : active_campaigns.map(c => (
              <div
                key={c.id}
                className="feed-row clickable"
                onClick={() => navigate(`/campaigns/analytics/${c.id}`)}
              >
                <div className="feed-time mono">{c.progress.toFixed(0)}%</div>
                <div className="feed-body">
                  <div className="feed-title">{c.name}</div>
                  <div className="bar">
                    <div className="bar-fill" style={{ width: `${c.progress}%` }} />
                  </div>
                  <div className="feed-meta">
                    {fmt(c.sent)} sent · {fmt(c.pending)} pending · {fmt(c.total_contacts)} contacts
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <div className="panel-title">
              <Inbox size={14} strokeWidth={1.75} /> Activity feed
              <span className="live-pulse"><span className="live-dot" /> live</span>
            </div>
            <button className="link-btn" onClick={() => navigate('/inbox')}>
              Inbox <ArrowUpRight size={12} />
            </button>
          </div>
          <div className="feed">
            {recent_replies.length === 0 ? (
              <div className="empty">No replies yet</div>
            ) : recent_replies.map(r => (
              <div key={r.id} className="feed-row">
                <div className="feed-time mono">
                  {r.created_at ? new Date(r.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                </div>
                <div className="feed-body">
                  <div className="feed-title">
                    <span className="feed-from">{r.from_name || r.from_email}</span>
                    {r.tag && (
                      <span className={`mini-tag tag-${r.sentiment || 'neutral'}`}>{r.tag}</span>
                    )}
                  </div>
                  <div className="feed-meta">{r.subject || '(no subject)'}</div>
                  {r.preview && <div className="feed-preview">{r.preview}</div>}
                </div>
                <div className="feed-tail mono">{r.from_email?.split('@')[1] || ''}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ── Queue strip (only if relevant) ── */}
      {(queue.pending > 0 || queue.failed > 0) && (
        <section className="panel queue-strip">
          <div className="panel-head">
            <div className="panel-title">
              <Clock size={14} strokeWidth={1.75} /> Queue
            </div>
          </div>
          <div className="queue-row">
            <div className="q-cell">
              <span className="q-dot warn" />
              <span className="q-val">{fmt(queue.pending)}</span>
              <span className="q-lbl">pending</span>
            </div>
            <div className="q-cell">
              <span className="q-dot ok" />
              <span className="q-val">{fmt(queue.sent_today)}</span>
              <span className="q-lbl">sent today</span>
            </div>
            {queue.failed > 0 && (
              <div className="q-cell">
                <span className="q-dot err" />
                <span className="q-val">{fmt(queue.failed)}</span>
                <span className="q-lbl">failed</span>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  )
}

function KpiTile({ label, value, delta, tone = 'primary' }) {
  return (
    <div className={`kpi-tile tone-${tone}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-delta">{delta}</div>
    </div>
  )
}

function StripCell({ label, value, icon: Icon, onClick }) {
  return (
    <div className={`strip-cell ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      <Icon size={14} strokeWidth={1.75} className="strip-icon" />
      <span className="strip-label">{label}</span>
      <span className="strip-value">{value}</span>
    </div>
  )
}

export default Dashboard
